import { $t } from 'src/plugins/i18n'
import { relationsEnumRule } from 'src/services/mappings/files.utils'
import {
  enumLabelIsKey,
  maxHundredPercentRules,
} from 'src/services/mappings/mapping.utils'
import {
  PORTFOLIO_COUNTRY_CODES,
  PORTFOLIO_ESTABLISHMENT_TYPES,
  PORTFOLIO_UX_CONTEXTS,
  PRODUCT_CATEGORY_PARENTS,
} from 'src/services/mappings/portfolio/portfolio.const'
import {
  getOwner,
  getPortfolioProductKindEnumLabel,
  getPortfolioProductKindEnums,
  getProductCategoryEnumLabel,
  getProductGroupColor,
  getProductGroupEnumLabel,
} from 'src/services/mappings/portfolio/portfolio.mapping.utils'
import { portfolioAmountFieldLabel } from 'src/services/mappings/portfolio/portfolio.utils'
import type {
  DynamicEnumsRuleInterface,
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { OBJECT_TYPES } from 'src/tpbp-configuration/models/models.const'
import { ProviderName } from 'src/tpbp-core/models/common.const'
import {
  ENUM_SORTING_RULE,
  FIELD_SET_DISPLAY_TYPES,
  FIELD_TYPES,
} from 'src/tpbp-core/models/fields/field-set.const'
import {
  FIELD_RULES,
  NO_BANK_NAME_CATEGORIES,
} from 'src/tpbp-core/models/fields/fieldRules.const'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import { type EnumInterface } from 'src/tpbp-core/models/global.type'
import { valueIsEmpty } from 'src/tpbp-core/utils/common.utils'
import { NUMBER_FORMATS } from 'src/tpbp-core/utils/formatter/formatter.const'
import { type PortfolioModel } from 'src/tpbp-json-api-handler/models/models'
import _ from 'underscore'
import type { PortfolioModelMeta } from './portfolio.types'

const isLinxoPortfolio = (portfolio) =>
  portfolio.providerName === ProviderName.linxo

const isDisabledAttributeRule = {
  type: FIELD_RULES.isReadOnly,
  expression: (
    record,
    { portfolioIsReadOnly, fieldStatusesByOriginKey },
    { originKey },
  ) => portfolioIsReadOnly(record, originKey, fieldStatusesByOriginKey),
}

const nameLinkedDynamicEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record) => {
      const parent = record?.pseudoParent as PortfolioModel
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (!parent || !parent?._linkedPortfolios) {
        return []
      }
      const linkedPortfoliosById = _.indexBy(
        parent?._linkedPortfolios ?? [],
        'id',
      )

      const options = parent._linkablePortfolios
        .filter(
          ({ id, $isNew }) =>
            !$isNew && !Object.hasOwn(linkedPortfoliosById, id),
        )
        .map<EnumInterface>(({ id: key, name: label }) => ({
          key,
          label,
        }))
      return options
    },
  },
]

const contextualDynamicRules = [
  isDisabledAttributeRule,
  {
    type: FIELD_RULES.isMandatory,
    expression: (
      record,
      { portfolioIsMandatory, fieldStatusesByOriginKey },
      { originKey },
    ) => portfolioIsMandatory(record, originKey, fieldStatusesByOriginKey),
  },
  {
    type: FIELD_RULES.isOptional,
    expression: (
      record,
      { portfolioIsOptional, fieldStatusesByOriginKey },
      { originKey },
    ) => portfolioIsOptional(record, originKey, fieldStatusesByOriginKey),
  },
]

const contextualDynamicReadonlyRules = [
  isDisabledAttributeRule,
  {
    type: FIELD_RULES.isReadOnly,
    expression: (
      record,
      { portfolioIsOptional, portfolioIsMandatory, fieldStatusesByOriginKey },
      { originKey },
    ) =>
      portfolioIsMandatory(record, originKey, fieldStatusesByOriginKey) ||
      portfolioIsOptional(record, originKey, fieldStatusesByOriginKey),
  },
]

const globalSrriDynamicRules = [
  ...contextualDynamicRules,
  {
    expression: ({ $uxContext, isInternal }) =>
      $uxContext === PORTFOLIO_UX_CONTEXTS.inTable || isInternal,
    type: FIELD_RULES.isHidden,
  },
]

const esgDynamicRules = [
  ...contextualDynamicRules,
  {
    expression: ({ $uxContext, isInternal, providerName }) => {
      return (
        $uxContext === PORTFOLIO_UX_CONTEXTS.inTable ||
        isInternal ||
        providerName === ProviderName.linxo
      )
    },
    type: FIELD_RULES.isHidden,
  },
]

const taxDateDynamicRules = [
  ...contextualDynamicRules,
  {
    expression: ({ $uxContext, providerName }) => {
      return (
        $uxContext === PORTFOLIO_UX_CONTEXTS.inTable ||
        providerName === ProviderName.linxo
      )
    },
    type: FIELD_RULES.isHidden,
  },
]

const linkedPortfolioDynamicRules = [
  isDisabledAttributeRule,
  {
    type: FIELD_RULES.isMandatory,
    expression: (
      record,
      { portfolioIsMandatory, fieldStatusesByOriginKey },
      { originKey },
    ) => portfolioIsMandatory(record, originKey, fieldStatusesByOriginKey),
  },
  {
    type: FIELD_RULES.isOptional,
    expression: (
      record,
      { portfolioIsOptional, fieldStatusesByOriginKey },
      { originKey },
    ) => portfolioIsOptional(record, originKey, fieldStatusesByOriginKey),
  },
  {
    type: FIELD_RULES.isReadOnly,
    expression: (record) =>
      record.$uxContext === PORTFOLIO_UX_CONTEXTS.linkedPortfolio,
  },
]

const contextualOwnerNewRules = [
  {
    type: FIELD_RULES.isMandatory,
    expression: ({ $isNew, $uxContext, productKind }) => {
      return (
        $uxContext === PORTFOLIO_UX_CONTEXTS.inTable &&
        $isNew &&
        Boolean(productKind)
      )
    },
  },
]

export const contextualOwnerRules = [
  {
    type: FIELD_RULES.isReadOnly,
    expression: ({ $isNew, $uxContext, productKind }) => {
      return (
        $uxContext === PORTFOLIO_UX_CONTEXTS.inTable &&
        !$isNew &&
        Boolean(productKind)
      )
    },
  },
]

const contextualDynamicEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record, { getPortfolioEnums }, field) =>
      getPortfolioEnums(record, field),
  },
]

const contextualDynamicOwnerNewEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record, { getOwnerNewEnums, currentUser }) =>
      getOwnerNewEnums(record, currentUser),
  },
]

const toTypeDynamicLabel = (record, { getEnumLabel }) =>
  getEnumLabel(record.$type, 'toType', record.toType)

const portfolioFields: FieldInterface[] = [
  {
    key: 'productKind',
    originKey: 'product_kind',
    type: FIELD_TYPES.string,
    enumType: 'ProductKind',
    enumTypePath: 'src/services/mappings/portfolio/portfolio.const',
    dynamicRules: [
      isDisabledAttributeRule,
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: PortfolioModel) => !record.$isNew,
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: PortfolioModel) =>
          record.$isNew &&
          (Boolean(record.productGroupMacro) ||
            Boolean(record.productGroup) ||
            Boolean(record.productGroupFromProductKind) ||
            Boolean(record.productCategoryNew)),
      },
    ],
    enumKeys: [],
    getEnums: getPortfolioProductKindEnums,
    getEnumLabel: getPortfolioProductKindEnumLabel,
  },
  {
    key: 'productCategory',
    originKey: 'product_category',
    type: FIELD_TYPES.string,
    enumType: 'PRODUCT_CATEGORY',
    enumTypePath: 'src/services/mappings/portfolio/portfolio.const',
    dynamicRules: [
      isDisabledAttributeRule,
      {
        type: FIELD_RULES.isHidden,
        expression: (record: PortfolioModel) => !record.$isNew,
      },
    ],
    getEnumLabel: getProductCategoryEnumLabel,
  },
  {
    key: 'productCategoryNew',
    originKey: 'product_category',
    type: FIELD_TYPES.string,
    dynamicRules: [
      isDisabledAttributeRule,
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.inTable && !record.$isNew,
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: PortfolioModel) => record.$isNew,
      },
    ],
    isPseudoAttribute: true,
    enumRules: contextualDynamicEnumRules,
    enumSortingRule: ENUM_SORTING_RULE.alphabeticalLabel,
    getEnumLabel: getProductCategoryEnumLabel,
  },
  {
    key: 'productCategoryParent',
    originKey: 'productCategoryParent',
    isPseudoAttribute: true,
    type: FIELD_TYPES.string,
    enumKeys: Object.values(PRODUCT_CATEGORY_PARENTS),
    rule: FIELD_RULES.isHidden,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'productGroupMacro',
    originKey: 'product_group_macro',
    isPseudoAttribute: true,
    type: FIELD_TYPES.string,
    enumKeys: [],
    enumType: 'PortfolioConfigurationGroupMacro',
    enumTypePath: 'src/services/mappings/portfolio/portfolio.types',
    rule: FIELD_RULES.isHidden,
  },
  {
    key: 'isNewContract',
    originKey: 'is_new_contract',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isHidden,
  },
  {
    key: 'name',
    originKey: 'name',
    type: FIELD_TYPES.string,
    dynamicRules: [
      isDisabledAttributeRule,
      {
        expression: (record: PortfolioModel) => Boolean(record.productKind),
        type: FIELD_RULES.isMandatory,
      },
      {
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.linkedPortfolio &&
          !record.$isNew,
        type: FIELD_RULES.isReadOnly,
      },
      {
        expression: ({ isInternal }: PortfolioModel) => isInternal,
        type: FIELD_RULES.isReadOnly,
      },
    ],
  },
  {
    key: 'nameLinked',
    originKey: 'nameLinked',
    type: FIELD_TYPES.string,
    isPseudoAttribute: true,
    enumRules: nameLinkedDynamicEnumRules,
    dynamicRules: [
      {
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.linkedPortfolio &&
          record.$isNew,
        type: FIELD_RULES.isMandatory,
      },
      {
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.linkedPortfolio &&
          !record.$isNew,
        type: FIELD_RULES.isHidden,
      },
    ],
  },
  {
    key: 'pseudoParent',
    originKey: 'pseudoParent',
    type: FIELD_TYPES.object,
    isPseudoAttribute: true,
  },
  {
    key: 'productGroup',
    originKey: 'product_group',
    type: FIELD_TYPES.string,
    dynamicRules: [
      isDisabledAttributeRule,
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.inTable && !record.$isNew,
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: PortfolioModel) => record.$isNew,
      },
    ],
    isPseudoAttribute: true,
    enumRules: contextualDynamicEnumRules,
    getEnumLabel: getProductGroupEnumLabel,
  },
  {
    key: 'productGroupFromProductKind',
    originKey: 'product_group',
    type: FIELD_TYPES.string,
    isPseudoAttribute: true,
    enumRules: contextualDynamicEnumRules,
    getEnumLabel: getProductGroupEnumLabel,
    expression: (record: PortfolioModel, { getPortfolioProductGroup }) => {
      return getPortfolioProductGroup(record.productKind) ?? ''
    },
  },
  {
    key: 'productGroupColor',
    originKey: 'product_group_color',
    type: FIELD_TYPES.string,
    enumType: 'TailwindColor',
    enumTypePath: 'src/utils/getColor',
    isPseudoAttribute: true,
    expression: (record: PortfolioModel, { getPortfolioProductGroup }) => {
      const productGroup = getPortfolioProductGroup(record.productKind)
      return productGroup ? getProductGroupColor(productGroup) : undefined
    },
  },
  {
    key: 'amount',
    originKey: 'amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicLabel: portfolioAmountFieldLabel,
    dynamicRules: [
      isDisabledAttributeRule,
      {
        expression: (record: PortfolioModel) => Boolean(record.productKind),
        type: FIELD_RULES.isMandatory,
      },
      {
        expression: (record: PortfolioModel) =>
          record.$uxContext === PORTFOLIO_UX_CONTEXTS.linkedPortfolio ||
          isLinxoPortfolio(record),
        type: FIELD_RULES.isReadOnly,
      },
      {
        expression: ({ isInternal }: PortfolioModel) => isInternal,
        type: FIELD_RULES.isReadOnly,
      },
    ],
    controls: {
      min: () => 0,
    },
  },
  {
    key: 'owner',
    originKey: 'owner',
    type: FIELD_TYPES.string,
    dynamicRules: contextualOwnerRules,
    isPseudoAttribute: true,
    expression: getOwner,
  },
  {
    key: 'ownership_type',
    originKey: 'ownership_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualOwnerNewRules,
    enumRules: contextualDynamicOwnerNewEnumRules,
    enumKeys: [],
    isPseudoAttribute: true,
  },
  {
    key: 'bankName',
    originKey: 'bank_name',
    type: FIELD_TYPES.string,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [],
    dynamicRules: [
      isDisabledAttributeRule,
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: PortfolioModel) =>
          !record.$isNew &&
          Boolean(record.productKind) &&
          !NO_BANK_NAME_CATEGORIES[record.productCategory],
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: PortfolioModel, { isBankNamePresent }) =>
          record.$isNew &&
          Boolean(record.productKind) &&
          !NO_BANK_NAME_CATEGORIES[record.productCategory] &&
          isBankNamePresent(record),
      },
    ],
    dynamicEnumLabel: enumLabelIsKey,
    isClearable: true,
  },
  {
    key: 'fromDate',
    originKey: 'from_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
    controls: {
      max: (_record, { TODAY }) => TODAY,
    },
  },
  {
    key: 'fromAmount',
    originKey: 'from_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: linkedPortfolioDynamicRules,
  },
  {
    key: 'toType',
    originKey: 'to_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'term',
      'disposal_date',
      'at_date',
      'at_age',
      'duration_year',
      'retirement',
      'option_exercise_date',
    ],
    isClearable: true,
  },
  {
    key: 'toAmount',
    originKey: 'to_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'establishmentType',
    originKey: 'establishment_type',
    type: FIELD_TYPES.string,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      PORTFOLIO_ESTABLISHMENT_TYPES.nonSignificative,
      PORTFOLIO_ESTABLISHMENT_TYPES.internal,
      PORTFOLIO_ESTABLISHMENT_TYPES.external,
    ],
  },
  {
    key: 'revaluationRateYearly',
    originKey: 'revaluation_rate_yearly',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'transferExemption',
    originKey: 'transfer_exemption',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'none',
      'dutreil_pact',
      'monument',
      'new_building_in_periods_between_1993_1995',
      'old_building_in_period_between_1995_1996',
    ],
  },
  {
    key: 'toDate',
    originKey: 'to_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
    dynamicLabel: toTypeDynamicLabel,
  },
  {
    key: 'plan',
    originKey: 'plan',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'besson_logement_neuf',
      'borloo_logement_neuf',
      'robien_classique',
      'robien_recentre',
      'girardin_logement_neuf',
      'scellier_dom_tom',
      'scellier_bbc',
      'scellier',
      'denormandie_outre_mer',
      'denormandie',
      'pinel_outre_mer',
      'pinel',
      'borloo_convention_anah',
      'ordinaire',
      'micro_foncier',
      'besson_logement_ancien',
      'malraux_depuis_2009',
      'duflot',
      'duflot_outre_mer',
      'demessine_logement_neuf',
      'perissol',
      'monuments_historiques',
      'nue_propriete',
    ],
  },
  {
    key: 'exitAnnuityPct',
    originKey: 'exit_annuity_pct',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxationGivenEXO',
    originKey: 'taxation_given_EXO',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'exitInFractionedCapitalYears',
    originKey: 'exit_in_fractioned_capital_years',
    type: FIELD_TYPES.integer,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'disposalTaxableBase',
    originKey: 'disposal_taxable_base',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'disposalExemption',
    originKey: 'disposal_exemption',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'acquisitionFeeAmount',
    originKey: 'acquisition_fee_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'buildingConstructionCompletionDate',
    originKey: 'building_construction_completion_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'buildingReservationDate',
    originKey: 'building_reservation_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'buildingPermitDelivryDate',
    originKey: 'building_permit_delivry_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'buildingPermitIssuanceDate',
    originKey: 'building_permit_issuance_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'planSector',
    originKey: 'plan_sector',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['intermediaire', 'libre', 'social'],
  },
  {
    key: 'planDuration',
    originKey: 'plan_duration',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      '9',
      '12',
      '15',
      'commitment_6y',
      'commitment_6y_extension_3y',
      'commitment_6y_extension_6y',
      'commitment_9y',
      'commitment_9y_extension_3y',
    ],
  },
  {
    key: 'planPrecision',
    originKey: 'plan_precision',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'en_zrr',
      'hors_zrr',
      'ordinaire',
      'micro_foncier',
      'secteur_intermediaire',
    ],
  },
  {
    key: 'withVat',
    originKey: 'with_vat',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxSystem',
    originKey: 'tax_system',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'bic_non_pro_real_cga_member_plan',
      'bic_non_pro_real_cga_non_member_plan',
      'bic_non_pro_micro_plan',
      'bic_non_pro_micro_plan_tourism',
      'bic_pro_real_cga_member_plan',
      'bic_pro_real_cga_non_member_plan',
      'bic_pro_micro_regime',
    ],
  },
  {
    key: 'amortizationYears',
    originKey: 'amortization_years',
    type: FIELD_TYPES.integer,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'renewalAfterAmortization',
    originKey: 'renewal_after_amortization',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'exemptionIfiProfessionalGoods',
    originKey: 'exemption_ifi_professional_goods',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'acquisitionFeeType',
    originKey: 'acquisition_fee_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['deductible', 'depreciable'],
  },
  {
    key: 'toValue',
    originKey: 'to_value',
    type: FIELD_TYPES.integer,
    dynamicRules: contextualDynamicRules,
    dynamicLabel: toTypeDynamicLabel,
  },
  {
    key: 'exitType',
    originKey: 'exit_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['capital', 'annuity', 'transfer', 'capital_and_annuity'],
  },
  {
    key: 'insuredType',
    originKey: 'insured_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['mister', 'misses', 'joint_first_to_die', 'joint_second_to_die'],
  },
  {
    key: 'taxationGiven757B',
    originKey: 'taxation_given_757B',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxationGiven990I',
    originKey: 'taxation_given_990I',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxationGivenDate',
    originKey: 'taxation_given_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'lumpSumDeduction30',
    originKey: 'lump_sum_deduction_30',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'optionExerciseFees',
    originKey: 'option_exercise_fees',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'outstandingCharges',
    originKey: 'outstanding_charges',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'acquisitionReductionType',
    originKey: 'acquisition_reduction_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'none',
      'tpe_income_tax',
      'income_tax',
      'rate_30_percent',
      'rate_36_percent',
      'rate_48_percent',
    ],
  },
  {
    key: 'loanUpdatedAt',
    originKey: 'loan_updated_at',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'pctAssetsInvestedInSmeCapital',
    originKey: 'pct_assets_invested_in_sme_capital',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'blockedAmount',
    originKey: 'blocked_amount',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: (record) => [
      (v) =>
        valueIsEmpty(v) ||
        v <= record.amount ||
        $t('models.portfolios.errors.maxBlockedPart', {
          amount: record.amount || 0,
        }),
    ],
  },
  {
    key: 'perType',
    originKey: 'per_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['assurance_vie', 'compte_titres'],
  },
  {
    key: 'insuredCapital',
    originKey: 'insured_capital',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'returnOfPremiums',
    originKey: 'return_of_premiums',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'loanType',
    originKey: 'loan_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'non_depreciable_debt',
      'revolving',
      'incremental_terms',
      'in_fine',
      'constant_terms_without_insurance',
      'revisable_rate_constant_terms',
      'constant_terms',
      'revisable_rate_constant_terms_without_insurance',
    ],
  },
  {
    key: 'subscribedById',
    originKey: 'subscribed_by_id',
    targetKey: 'id',
    originRelationship: '_subscribedBy',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: relationsEnumRule,
  },
  {
    key: 'loanRate',
    originKey: 'loan_rate',
    type: FIELD_TYPES.number,
    format: NUMBER_FORMATS.percent2digits,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'deferredType',
    originKey: 'deferred_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['total', 'partial', 'none'],
  },
  {
    key: 'administrationFeesType',
    originKey: 'administration_fees_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['amount', 'percentage'],
  },
  {
    key: 'administrationFees',
    originKey: 'administration_fees',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: (record: PortfolioModel) => [
      (v) => {
        if (record.administrationFeesType === 'amount') {
          return true
        }

        return (
          v <= 100 ||
          $t('models.portfolios.errors.maxBlockedPart', { amount: 100 })
        )
      },
    ],
  },
  {
    key: 'insuranceRate',
    originKey: 'insurance_rate',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'coverageRate',
    originKey: 'coverage_rate',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'rateType',
    originKey: 'rate_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: ['proportional', 'actuarial'],
  },
  {
    key: 'currentRisk',
    originKey: 'current_risk',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'cr_garanti',
      'cr_prudent',
      'cr_defensif',
      'cr_equilibre',
      'cr_offensif',
      'cr_agressif',
    ],
  },
  {
    key: 'budgetImpact',
    originKey: 'budget_impact',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'monthlyPayment',
    originKey: 'monthly_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: [
      ...contextualDynamicReadonlyRules,
      {
        expression: ({ $uxContext, isInternal }: PortfolioModel) =>
          $uxContext !== PORTFOLIO_UX_CONTEXTS.inTable || isInternal,
        type: FIELD_RULES.isHidden,
      },
    ],
  },
  {
    key: 'remainingOwedCapital',
    originKey: 'remaining_owed_capital',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'revisedRate',
    originKey: 'revised_rate',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'loanCeiling',
    originKey: 'loan_ceiling',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'outstandingAmount',
    originKey: 'outstanding_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'capitalizedInterests',
    originKey: 'capitalized_interests',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'deferredMonths',
    originKey: 'deferred_months',
    type: FIELD_TYPES.integer,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'deferredCapitalizedInterests',
    originKey: 'deferred_capitalized_interests',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'purgedAtBeginningOfAmortization',
    originKey: 'purged_at_beginning_of_amortization',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isInternal',
    originKey: 'is_internal',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'providerName',
    originKey: 'provider_name',
    enumType: 'ProviderName',
    enumTypePath: 'src/tpbp-core/models/common.const',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'beneficiaryClause',
    originKey: 'beneficiary_clause',
    type: FIELD_TYPES.string,
    enumKeys: [],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'globalSrriRiskLevel',
    originKey: 'global_srri_risk_level',
    type: FIELD_TYPES.string,
    dynamicRules: [
      ...contextualDynamicRules,
      {
        expression: ({
          $uxContext,
          isInternal,
          providerName,
        }: PortfolioModel) => {
          return (
            $uxContext !== PORTFOLIO_UX_CONTEXTS.inTable ||
            isInternal ||
            providerName === ProviderName.linxo
          )
        },
        type: FIELD_RULES.isHidden,
      },
      {
        expression: (
          record: PortfolioModel,
          { portfolioIsOptional, fieldStatusesByOriginKey },
          { originKey },
        ) => {
          const { $uxContext, $isNew } = record

          return (
            $isNew &&
            $uxContext === PORTFOLIO_UX_CONTEXTS.inTable &&
            portfolioIsOptional(record, originKey, fieldStatusesByOriginKey)
          ) // consider mandatory when optional in configuration
        },
        type: FIELD_RULES.isMandatory,
      },
    ],
    enumKeys: [],
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'globalSrriLowRiskRate',
    originKey: 'global_srri_low_risk_rate',
    type: FIELD_TYPES.number,
    dynamicRules: globalSrriDynamicRules,
    attachedFieldTag: 'global_srri_risk_rate',
  },
  {
    key: 'globalSrriMediumRiskRate',
    originKey: 'global_srri_medium_risk_rate',
    type: FIELD_TYPES.number,
    dynamicRules: globalSrriDynamicRules,
    attachedFieldTag: 'global_srri_risk_rate',
  },
  {
    key: 'globalSrriHighRiskRate',
    originKey: 'global_srri_high_risk_rate',
    type: FIELD_TYPES.number,
    dynamicRules: globalSrriDynamicRules,
    attachedFieldTag: 'global_srri_risk_rate',
  },
  {
    key: 'pledgeAccount',
    originKey: 'pledge_account',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'eligibleUniverse',
    originKey: 'eligible_universe',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['multi', 'mono'],
  },
  {
    key: 'address',
    originKey: 'address',
    type: FIELD_TYPES.string,
    displayType: FIELD_SET_DISPLAY_TYPES.autocompleteAddress,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'longitude',
    originKey: 'longitude',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'latitude',
    originKey: 'latitude',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'surface',
    originKey: 'surface',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'propertyType',
    originKey: 'property_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['house', 'apartment'],
  },
  {
    key: 'dealType',
    originKey: 'deal_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['sale', 'rent'],
  },
  {
    key: 'countryCode',
    originKey: 'country_code',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: Object.values(PORTFOLIO_COUNTRY_CODES),
  },
  {
    key: 'buildingYear',
    originKey: 'building_year',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'balconyArea',
    originKey: 'balcony_area',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'landArea',
    originKey: 'land_area',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'nbOfIndoorParkingSpaces',
    originKey: 'nb_of_indoor_parking_spaces',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'nbOfOutdoorParkingSpaces',
    originKey: 'nb_of_outdoor_parking_spaces',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'notaryFees',
    originKey: 'notary_fees',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'numberOfBathrooms',
    originKey: 'number_of_bathrooms',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'hasLift',
    originKey: 'has_lift',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'hasSauna',
    originKey: 'has_sauna',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'hasPool',
    originKey: 'has_pool',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isFurnished',
    originKey: 'is_furnished',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isNew',
    originKey: 'is_new',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'condition',
    originKey: 'condition',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'conditionBathroom',
    originKey: 'condition_bathroom',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'conditionFlooring',
    originKey: 'condition_flooring',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'conditionKitchen',
    originKey: 'condition_kitchen',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'conditionWindows',
    originKey: 'condition_windows',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'conditionMasonry',
    originKey: 'condition_masonry',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [
      'renovation_needed',
      'well_maintained',
      'new_or_recently_renovated',
    ],
  },
  {
    key: 'quality',
    originKey: 'quality',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'qualityBathroom',
    originKey: 'quality_bathroom',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'qualityFlooring',
    originKey: 'quality_flooring',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'qualityKitchen',
    originKey: 'quality_kitchen',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'qualityWindows',
    originKey: 'quality_windows',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'qualityMasonry',
    originKey: 'quality_masonry',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: ['simple', 'normal', 'high_quality', 'luxury'],
  },
  {
    key: 'nbRooms',
    originKey: 'nb_rooms',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'gardenSurface',
    originKey: 'garden_surface',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'floors',
    originKey: 'floors',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'nbFloors',
    originKey: 'nb_floors',
    type: FIELD_TYPES.number,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'allocation',
    originKey: 'allocation',
    type: FIELD_TYPES.object,
    objectType: 'PortfolioAllocation',
    objectTypePath: 'src/services/mappings/portfolio/portfolio.types',
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'lclExternalId',
    originKey: 'lcl_external_id',
    type: FIELD_TYPES.string,
  },
  {
    key: 'caaExternalId',
    originKey: 'caa_external_id',
    type: FIELD_TYPES.string,
  },
  {
    key: 'caTitresExternalId',
    originKey: 'ca_titres_external_id',
    type: FIELD_TYPES.string,
  },
  {
    key: 'lclIdentifierExternalId',
    originKey: 'lcl_identifier_external_id',
    type: FIELD_TYPES.string,
  },
  {
    key: 'lclProducerContractExternalId',
    originKey: 'lcl_producer_contract_external_id',
    type: FIELD_TYPES.string,
  },
  {
    key: 'productType',
    originKey: 'product_type',
    type: FIELD_TYPES.string,
  },
  {
    key: 'uomCode',
    originKey: 'uom_code',
    type: FIELD_TYPES.string,
  },
  {
    key: 'createdAt',
    originKey: 'created_at',
    type: FIELD_TYPES.date,
  },
  {
    key: 'subscribedAt',
    originKey: 'subscribed_at',
    type: FIELD_TYPES.date,
  },
  {
    key: 'label',
    originKey: 'label',
    type: FIELD_TYPES.string,
  },
  {
    key: 'buildingPermitDeliveryDate',
    originKey: 'building_permit_delivery_date',
    type: FIELD_TYPES.number,
  },
  {
    key: 'ceilingLoanMimt',
    originKey: 'ceiling_loan_limt',
    type: FIELD_TYPES.number,
  },
  {
    key: 'contractFeesUpdatedAt',
    originKey: 'contract_fees_updated_at',
    type: FIELD_TYPES.date,
  },
  {
    key: 'currency',
    originKey: 'currency',
    type: FIELD_TYPES.string,
  },
  {
    key: 'customSrri',
    originKey: 'custom_srri',
    type: FIELD_TYPES.number,
  },
  {
    key: 'depositFee',
    originKey: 'deposit_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'detentionMode',
    originKey: 'detention_mode',
    type: FIELD_TYPES.string,
  },
  {
    key: 'entryFee',
    originKey: 'entry_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'euroFundsFees',
    originKey: 'euro_funds_fees',
    type: FIELD_TYPES.string,
    isMultiple: true,
  },
  {
    key: 'exitFee',
    originKey: 'exit_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'initialAmount',
    originKey: 'initial_amount',
    type: FIELD_TYPES.number,
  },
  {
    key: 'location',
    originKey: 'location',
    type: FIELD_TYPES.number,
  },
  {
    key: 'managementFee',
    originKey: 'management_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'monthlyBuyout',
    originKey: 'monthly_buyout',
    type: FIELD_TYPES.number,
  },
  {
    key: 'monthlyDeposit',
    originKey: 'monthly_deposit',
    type: FIELD_TYPES.number,
  },
  {
    key: 'planCategory',
    originKey: 'plan_category',
    type: FIELD_TYPES.number,
  },
  {
    key: 'plusOrMinusValue',
    originKey: 'plus_or_minus_value',
    type: FIELD_TYPES.number,
  },
  {
    key: 'recommendedRisk',
    originKey: 'recommended_risk',
    type: FIELD_TYPES.number,
  },
  {
    key: 'reductionGirardin',
    originKey: 'reduction_girardin',
    type: FIELD_TYPES.number,
  },
  {
    key: 'transferFee',
    originKey: 'transfer_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'ucsFee',
    originKey: 'ucs_fee',
    type: FIELD_TYPES.number,
  },
  {
    key: 'cumulatedPaymentAmount',
    originKey: 'cumulated_payment_amount',
    type: FIELD_TYPES.number,
  },
  {
    key: 'deductions',
    originKey: 'deductions',
    type: FIELD_TYPES.number,
  },
  {
    key: 'deductionsComputed',
    originKey: 'deductions_computed',
    type: FIELD_TYPES.number,
  },
  {
    key: 'taxDate',
    originKey: 'tax_date',
    type: FIELD_TYPES.date,
    dynamicRules: taxDateDynamicRules,
  },
  {
    key: 'esgSfdr',
    originKey: 'esg_sfdr',
    type: FIELD_TYPES.string,
    enumRules: contextualDynamicEnumRules,
    dynamicRules: esgDynamicRules,
    enumKeys: [],
  },
  {
    key: 'esgTaxonomy',
    originKey: 'esg_taxonomy',
    type: FIELD_TYPES.string,
    enumRules: contextualDynamicEnumRules,
    dynamicRules: esgDynamicRules,
    enumKeys: [],
  },
  {
    key: 'esgGreenhouseGasesExposure',
    originKey: 'esg_greenhouse_gases_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'esgBiodiversityExposure',
    originKey: 'esg_biodiversity_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'esgWaterPollutionExposure',
    originKey: 'esg_water_pollution_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'esgHazardousWasteExposure',
    originKey: 'esg_hazardous_waste_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'esgHumanRightsExposure',
    originKey: 'esg_human_rights_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'esgGenderEqualityExposure',
    originKey: 'esg_gender_equality_exposure',
    type: FIELD_TYPES.boolean,
    dynamicRules: esgDynamicRules,
  },
  {
    key: 'beneficiaryClauseModifiedAt',
    originKey: 'beneficiary_clause_modified_at',
    type: FIELD_TYPES.date,
  },
  {
    key: 'transmissionWarning',
    originKey: 'transmission_warning',
    type: FIELD_TYPES.object,
    isPseudoAttribute: true,
    isMultiple: true,
    objectType: 'PortfolioModelMetaWarnings',
    objectTypePath: 'src/services/mappings/portfolio/portfolio.types',
    expression: (record: PortfolioModel) => {
      const meta = record.$meta as unknown as PortfolioModelMeta
      return (
        meta.warnings?.filter(
          (warning) =>
            warning.meta.attribute === 'beneficiary_clause' ||
            warning.meta.attribute === 'taxation_given_757B' ||
            warning.meta.attribute === 'beneficiary_clause_modified_at',
        ) ?? []
      )
    },
  },
  {
    key: 'currentYearDeductableDeposits',
    originKey: 'current_year_deductable_deposits',
    type: FIELD_TYPES.number,
  },
]

const portfolioRelations: RelationInterface[] = [
  {
    key: '_beneficiaries',
    originKey: 'beneficiaries',
    objectType: OBJECT_TYPES.beneficiaries,
    isMultiple: true,
  },
  {
    key: '_budgetItems',
    originKey: 'budget_items',
    objectType: OBJECT_TYPES.budgetItem,
    isMultiple: true,
  },
  {
    key: '_holders',
    originKey: 'holders',
    objectType: OBJECT_TYPES.person,
    isMultiple: true,
    dependentFieldKeys: ['owner'],
  },
  {
    key: '_households',
    originKey: 'households',
    objectType: OBJECT_TYPES.household,
    isMultiple: true,
  },
  {
    key: '_lines',
    originKey: 'lines',
    objectType: OBJECT_TYPES.lines,
    isMultiple: true,
  },
  {
    key: '_nonAttributedOwnershipParts',
    originKey: 'non_attributed_ownership_parts',
    isMeta: true,
  },
  {
    key: '_ownerships',
    originKey: 'ownerships',
    objectType: OBJECT_TYPES.ownership,
    isMultiple: true,
  },
  {
    key: '_subscribedBy',
    originKey: 'subscribed_by',
    objectType: OBJECT_TYPES.person,
    isMultiple: false,
  },
  {
    key: '_linkablePortfolios',
    originKey: 'linkable_portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_linkedPortfolios',
    originKey: 'linked_portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_linkableBudgetItems',
    originKey: 'linkable_budget_items',
    objectType: OBJECT_TYPES.budgetItem,
    isMultiple: true,
  },
  {
    key: '_linkedBudgetItems',
    originKey: 'linked_budget_items',
    objectType: OBJECT_TYPES.budgetItem,
    isMultiple: true,
  },
  {
    key: '_saleEstimateSeries',
    originKey: 'sale_estimate_series',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_saleEstimateSeriesMin',
    originKey: 'sale_estimate_series_min',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_saleEstimateSeriesMax',
    originKey: 'sale_estimate_series_max',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_saleEstimate',
    originKey: 'sale_estimate',
    objectType: OBJECT_TYPES.estimate,
    isMultiple: false,
  },
  {
    key: '_rentEstimateSeries',
    originKey: 'rent_estimate_series',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_rentEstimateSeriesMin',
    originKey: 'rent_estimate_series_min',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_rentEstimateSeriesMax',
    originKey: 'sale_estimate_series_max',
    objectType: OBJECT_TYPES.estimateTimeseries,
    isMultiple: false,
  },
  {
    key: '_rentEstimate',
    originKey: 'rent_estimate',
    objectType: OBJECT_TYPES.estimate,
    isMultiple: false,
  },
]

export { portfolioFields, portfolioRelations }
